import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Users,
  UserPlus,
  Phone,
  Star,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  Building,
  MapPin,
  Briefcase,
  Calendar,
  MessageSquare,
  Mail,
  Send,
  X,
  AlertCircle,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration with colors and labels
const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  contacted: { label: 'Contacted', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  replied: { label: 'Replied', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  interested: { label: 'Interested', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  call_scheduled: { label: 'Call Scheduled', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  closed_won: { label: 'Closed Won', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  closed_lost: { label: 'Closed Lost', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
};

// Lead Detail Modal Component
const LeadDetailModal = ({ lead, isOpen, onClose, onStatusChange }) => {
  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-semibold text-white">{lead.company_name}</h2>
            <p className="text-slate-400">{lead.contact_name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Email</p>
              <p className="text-white">{lead.email || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Title</p>
              <p className="text-white">{lead.title || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Country</p>
              <p className="text-white">{lead.country || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Industry</p>
              <p className="text-white">{lead.industry || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Source</p>
              <p className="text-white">{lead.source || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${STATUS_CONFIG[lead.status]?.color}`}>
                {STATUS_CONFIG[lead.status]?.label}
              </span>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Created
              </p>
              <p className="text-slate-300 text-sm">{new Date(lead.created_at).toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Phone className="w-3 h-3" /> Last Contacted
              </p>
              <p className="text-slate-300 text-sm">
                {lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase flex items-center gap-1 mb-2">
              <MessageSquare className="w-3 h-3" /> Notes
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4 min-h-[100px]">
              <p className="text-slate-300 text-sm whitespace-pre-wrap">
                {lead.notes || 'No notes yet.'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <p className="text-xs text-slate-500 text-center">
            Lead ID: {lead.id}
          </p>
        </div>
      </div>
    </div>
  );
};

// Send Email Modal Component
const SendEmailModal = ({ lead, isOpen, onClose, onSuccess }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lead) {
      setSubject(`Hello from Coinexus`);
      setBody(`<p>Dear ${lead.contact_name},</p>\n\n<p>Thank you for your interest in Coinexus.</p>\n\n<p>Best regards,<br>Coinexus Team</p>`);
    }
    setError('');
  }, [lead, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/admin/leads/${lead.id}/send-email`, {
        subject,
        body
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-500" />
              Send Email
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              To: {lead.contact_name} ({lead.email})
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              placeholder="Email subject"
              required
              data-testid="email-subject-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Message <span className="text-slate-500 text-xs">(HTML supported)</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none font-mono text-sm"
              placeholder="<p>Your message here...</p>"
              required
              data-testid="email-body-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Use HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;br&gt; for formatting
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="send-email-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminLeads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    interested: 0,
    closed: 0
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Modal
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Email Modal
  const [emailLead, setEmailLead] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Status update loading
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/leads?${params}`);
      let filteredLeads = response.data.leads;
      
      // Client-side search filter (company or contact name)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredLeads = filteredLeads.filter(lead => 
          lead.company_name.toLowerCase().includes(query) ||
          lead.contact_name.toLowerCase().includes(query)
        );
      }
      
      setLeads(filteredLeads);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      
      // Update stats from API response
      if (response.data.stats) {
        setStats({
          total: response.data.total,
          new: response.data.stats.new,
          contacted: response.data.stats.contacted,
          interested: response.data.stats.interested,
          closed: response.data.stats.closed
        });
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    setUpdatingStatusId(leadId);
    try {
      await axios.put(`${API_URL}/api/admin/leads/${leadId}/status`, {
        status: newStatus
      });
      fetchLeads();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowModal(true);
  };

  const handleSendEmail = (lead) => {
    setEmailLead(lead);
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    setSuccessMessage('Email sent successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    fetchLeads();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-slate-600/50 transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onStatusChange={handleStatusChange}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        lead={emailLead}
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
      />

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="leads-title">
          Leads
        </h1>
        <p className="text-slate-400">
          Manage and track your sales leads
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard 
          icon={Users} 
          label="Total Leads" 
          value={stats.total} 
          color="bg-slate-500/20 text-slate-400" 
        />
        <StatCard 
          icon={UserPlus} 
          label="New" 
          value={stats.new} 
          color="bg-blue-500/20 text-blue-400" 
        />
        <StatCard 
          icon={Phone} 
          label="Contacted" 
          value={stats.contacted} 
          color="bg-cyan-500/20 text-cyan-400" 
        />
        <StatCard 
          icon={Star} 
          label="Interested" 
          value={stats.interested} 
          color="bg-amber-500/20 text-amber-400" 
        />
        <StatCard 
          icon={CheckCircle} 
          label="Closed" 
          value={stats.closed} 
          color="bg-emerald-500/20 text-emerald-400" 
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              data-testid="leads-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search company or contact..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>
        </form>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-500" />
          <select
            data-testid="leads-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No leads found</p>
            <p className="text-slate-500 text-sm mt-1">
              {statusFilter || searchQuery ? 'Try adjusting your filters' : 'Create your first lead to get started'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4" /> Company
                      </div>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Contact
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Title
                      </div>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Country
                      </div>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Assigned To
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Last Contacted
                      </div>
                    </th>
                    <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-700/20 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-medium text-white">{lead.company_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300">{lead.contact_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400">{lead.title || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400">{lead.country || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative">
                          {updatingStatusId === lead.id ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                              <span className="text-slate-400 text-sm">Updating...</span>
                            </div>
                          ) : (
                            <select
                              value={lead.status}
                              onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                              data-testid={`status-select-${lead.id}`}
                              className={`px-2 py-1 text-xs font-medium rounded border bg-transparent cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500 ${STATUS_CONFIG[lead.status]?.color}`}
                            >
                              {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                                <option key={value} value={value} className="bg-slate-800 text-white">
                                  {config.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400">
                          {lead.assigned_user?.email || lead.assigned_to || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {formatDate(lead.last_contacted_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {lead.email && (
                            <button
                              onClick={() => handleSendEmail(lead)}
                              data-testid={`email-lead-${lead.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors text-sm"
                              title="Send Email"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewLead(lead)}
                            data-testid={`view-lead-${lead.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} leads
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-slate-400 text-sm">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
